import React, { useState } from 'react';
import {
  getPropertyListingMode,
  getPropertyPhotoUrls,
  getPropertyCardImage,
  getPropertyListedAt,
  formatPropertyDateTime,
  getEffectiveMinInvestment,
} from '../../utils/properties';
import { getRentMonthlyCostItems, getRentAllInMonthly } from '../../utils/submissions';
import {
  formatExactMoney,
  formatExactShareMoney,
  getCountry,
  normalizeCountryIso,
  isMultiMarketView,
} from '../../data/countries';
import CountryFlag from '../shared/CountryFlag';

const PropertyCard = ({ p, onClick, isFavorite, onToggleFav, isComparing, onToggleCompare, country }) => {
  const [hoverImg, setHoverImg] = useState(0);
  const isRent = getPropertyListingMode(p) === "rent";
  const sharesSold = p.sharesSold || 0;
  const isFullyFunded = p.status === "funded" || (p.shares > 0 && sharesSold >= p.shares);
  const progress = p.shares ? (sharesSold / p.shares) * 100 : 0;
  const pricePerShare = p.shares ? p.price / p.shares : 0;
  const gallery = getPropertyPhotoUrls(p);
  const monthlyRent = Number(p.monthlyRent) || 0;
  const showPropertyCountry = isMultiMarketView(country) && p.country;
  const propertyCountryMeta = showPropertyCountry ? getCountry(normalizeCountryIso(p.country)) : null;
  return (
    <div className="property-card" onClick={onClick}
      onMouseEnter={() => { if (gallery.length > 1) { let i = 0; const iv = setInterval(() => { i = (i + 1) % gallery.length; setHoverImg(i); }, 1200); window._cardInterval = iv; } }}
      onMouseLeave={() => { clearInterval(window._cardInterval); setHoverImg(0); }}>
      <div className="property-image" style={{backgroundImage:`url(${gallery[hoverImg] || p.image})`}}>
        <span className={`property-badge ${isRent ? "" : isFullyFunded ? "funded" : ""} ${!isRent && p.hot && !isFullyFunded ? "hot" : ""} ${p.landBanking ? "land" : ""}`} style={isRent ? { background: "var(--info)" } : undefined}>
          {isRent ? "For rent" : p.landBanking ? "Land Banking" : p.hot && !isFullyFunded ? "Hot Deal" : isFullyFunded ? "Fully Funded" : "Now Funding"}
        </span>
        <div className="property-card-actions">
          {onToggleCompare && (
            <button
              type="button"
              className={`property-compare ${isComparing ? "active" : ""}`}
              title={isComparing ? "Remove from compare" : "Add to compare"}
              aria-pressed={isComparing ? "true" : "false"}
              onClick={(e) => { e.stopPropagation(); onToggleCompare(p); }}
            >
              <i className="fas fa-right-left"></i>
            </button>
          )}
          <button type="button" className={`property-favorite ${isFavorite?"active":""}`} onClick={(e)=>{e.stopPropagation();onToggleFav(p.id);}}>
            <i className={`${isFavorite?"fas":"far"} fa-heart`}></i>
          </button>
        </div>
        {gallery.length > 1 && (
          <div className="gallery-indicator">
            {gallery.slice(0,5).map((_, i) => <div key={i} className={`gallery-dot ${i===hoverImg?"active":""}`} />)}
          </div>
        )}
        <div className="photo-count"><i className="fas fa-camera" style={{marginRight:"0.2rem"}}></i>{gallery.length}</div>
        {showPropertyCountry && (
          <span className="property-country-flag" title={propertyCountryMeta?.name || p.country} onClick={(e) => e.stopPropagation()}>
            <CountryFlag code={p.country} imgWidth={18} emojiSize="1rem" />
            <span className="country-label-long">{propertyCountryMeta?.name || p.country}</span>
          </span>
        )}
      </div>
      <div className="property-info">
        <div className="property-location"><i className="fas fa-map-marker-alt"></i> {p.location}</div>
        <h3 className="property-title">{p.title}</h3>
        {getPropertyListedAt(p) && (
          <p className="property-listed-at">
            <i className="fas fa-clock" aria-hidden="true"></i>
            Listed {formatPropertyDateTime(getPropertyListedAt(p))}
          </p>
        )}
        {isRent ? (
          <>
            <div className="property-price">
              <span className="property-price-label">Monthly rent</span>
              {monthlyRent > 0 ? `${formatExactMoney(monthlyRent, country)}/mo` : "Contact for rate"}
              {getRentMonthlyCostItems(p).length > 0 && (
                <span style={{ display: "block", fontSize: "0.82rem", fontWeight: 500, color: "var(--text-light)", marginTop: "0.2rem" }}>
                  From {formatExactMoney(getRentAllInMonthly(p), country)}/mo incl. costs
                </span>
              )}
            </div>
            <div className="property-metrics" role="group" aria-label="Rental details">
              <div className="metric"><label>Beds</label><span className="metric-value">{p.beds ?? "—"}</span></div>
              <div className="metric"><label>Baths</label><span className="metric-value">{p.baths ?? "—"}</span></div>
              <div className="metric"><label>Type</label><span className="metric-value">{p.type || "—"}</span></div>
            </div>
          </>
        ) : (
          <>
            <div className="property-price">
              <span className="property-price-label">Property price</span>
              {formatExactMoney(p.price, country)}
            </div>
            <div className="property-metrics" role="group" aria-label="Investment metrics">
              {p.projectedReturn != null && p.projectedReturn !== '' ? (
              <div className="metric">
                <label>Target return</label>
                <span className="metric-value metric-value--return">
                  <i className="fas fa-chart-line metric-return-icon" aria-hidden="true"></i>
                  {p.projectedReturn}%
                </span>
              </div>
              ) : null}
              <div className="metric">
                <label>Min. invest</label>
                <span className="metric-value">{formatExactMoney(getEffectiveMinInvestment(p), country)}</span>
              </div>
              <div className="metric">
                <label>Per share</label>
                <span className={`metric-value${pricePerShare >= 10000 ? " metric-value--long" : ""}`}>
                  {formatExactShareMoney(pricePerShare, country)}
                </span>
              </div>
            </div>
            <div className="progress-bar"><div className="progress-fill" style={{ width: `${progress}%` }}></div></div>
            <div className="progress-text">
              <span><strong>{sharesSold}</strong> / {p.shares} shares</span>
              <span>{progress.toFixed(1)}%</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PropertyCard;
